/**
 * Central de atendimento (Prompt 02): respostas rápidas, modelos da Meta,
 * acompanhamento, etiquetas, encaminhamento interno e busca global.
 */
import { z } from "zod";

export interface RespostaRapida {
  id: string;
  atalho: string;
  titulo: string;
  texto: string;
  pessoal: boolean;
  usos: number;
}

export const respostaRapidaSchema = z.object({
  atalho: z
    .string()
    .trim()
    .toLowerCase()
    .regex(/^[a-z0-9_-]{1,30}$/, "Use letras minúsculas, números, - ou _ (até 30)."),
  titulo: z.string().trim().min(1, "Informe um título.").max(80),
  texto: z.string().trim().min(1, "Escreva a mensagem.").max(4096),
  pessoal: z.boolean().default(false),
});
export type NovaRespostaRapida = z.infer<typeof respostaRapidaSchema>;

/** Variáveis aceitas nas respostas rápidas. */
export const VARIAVEIS_RESPOSTA = {
  "{nome}": "primeiro nome da cliente",
  "{vendedora}": "seu primeiro nome",
} as const;

export function aplicarVariaveis(
  texto: string,
  v: { nomeCliente: string | null; vendedora: string },
): string {
  const primeiro = (n: string | null) => (n ?? "").trim().split(/\s+/)[0] ?? "";
  const cliente = primeiro(v.nomeCliente);
  const t = texto.replaceAll("{vendedora}", primeiro(v.vendedora));
  // Sem nome conhecido: some com a variável e o espaço/vírgula antes dela.
  return cliente ? t.replaceAll("{nome}", cliente) : t.replace(/[ ,]*\{nome\}/g, "");
}

/** Modelo de mensagem aprovado na Meta (necessário fora da janela de 24h). */
export interface ModeloMensagem {
  nome: string;
  idioma: string;
  categoria: string;
  /** Texto do corpo com {{1}}, {{2}}... */
  corpo: string;
  cabecalho: string | null;
  rodape: string | null;
  variaveis: number;
  /** Cabeçalho de mídia ou botões dinâmicos: ainda não suportados no envio. */
  suportado: boolean;
}

export const enviarModeloSchema = z.object({
  idCliente: z.string().uuid(),
  nome: z.string().min(1),
  idioma: z.string().min(2),
  parametros: z.array(z.string().trim().min(1, "Preencha todas as variáveis.").max(1000)).max(20),
});

export function renderizarModelo(corpo: string, parametros: string[]): string {
  return corpo.replace(
    /\{\{(\d+)\}\}/g,
    (_m, n: string) => parametros[Number(n) - 1] ?? `{{${n}}}`,
  );
}

export const acompanharSchema = z.object({
  /** Nulo remove o acompanhamento. */
  em: z.string().datetime().nullable(),
  nota: z.string().trim().max(300).nullish(),
});

export const etiquetasConversaSchema = z.object({
  etiquetaIds: z.array(z.string().uuid()).max(20),
});

export const encaminharInternoSchema = z.object({
  usuarioId: z.string().uuid(),
  comentario: z.string().trim().min(1, "Escreva o que a pessoa precisa saber.").max(1000),
  mensagemId: z.string().uuid().nullish(),
});

export interface ResultadoBusca {
  contatoId: string;
  nome: string;
  telefone: string;
  erpClienteId: string | null;
  /** Conversa mais recente (aberta ou concluída), se houver acesso. */
  conversa: {
    id: string;
    status: "aberta" | "aguardando_cliente" | "encerrada";
    ultimaMensagemEm: string | null;
    responsavel: string | null;
  } | null;
  /** Trecho de mensagem que casou com a busca. */
  trecho: string | null;
}

/** Tipos de arquivo aceitos pela Meta, por tipo de mensagem, e o tamanho máximo. */
export const LIMITES_MIDIA = {
  imagem: { mimes: ["image/jpeg", "image/png"], maxBytes: 5 * 1024 * 1024 },
  video: { mimes: ["video/mp4", "video/3gpp"], maxBytes: 16 * 1024 * 1024 },
  audio: {
    mimes: ["audio/ogg", "audio/mpeg", "audio/mp4", "audio/aac", "audio/amr", "audio/webm"],
    maxBytes: 16 * 1024 * 1024,
  },
  documento: { mimes: [] as string[], maxBytes: 100 * 1024 * 1024 },
} as const;

/** Decide o tipo de mensagem pelo mime (o que não é imagem/vídeo/áudio vai como documento). */
export function tipoPorMime(mime: string): keyof typeof LIMITES_MIDIA {
  const base = mime.split(";")[0]!.trim().toLowerCase();
  if ((LIMITES_MIDIA.imagem.mimes as readonly string[]).includes(base)) return "imagem";
  if ((LIMITES_MIDIA.video.mimes as readonly string[]).includes(base)) return "video";
  if (base.startsWith("audio/")) return "audio";
  return "documento";
}
